import { CellContext, ColumnDef, SortingFn } from '@tanstack/react-table'
import { ColumnGroup, EnrichedTeamMembership } from './types'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { cn } from '@/lib/utils'
import { Medal } from 'lucide-react'
import { useMemo } from 'react'
import { useAudit } from './context'

export const rankToColor = (value?: number) => {
  if (!value) {
    return ''
  }

  if (value === 1) {
    return 'text-quality-legendary'
  }
  if (value >= 2 && value <= 5) {
    return 'text-quality-epic'
  }
  if (value >= 6 && value <= 10) {
    return 'text-quality-rare'
  }
  if (value >= 11 && value <= 15) {
    return 'text-quality-uncommon'
  }
  return 'text-muted-foreground'
}

export const gearStats = [
  { value: 'crit', label: 'Crit', fullLabel: 'Critical strike' },
  { value: 'haste', label: 'Haste', fullLabel: 'Haste' },
  { value: 'mastery', label: 'Mast', fullLabel: 'Mastery' },
  { value: 'vers', label: 'Vers', fullLabel: 'Versatility' },
] as const

export const statPercentageToColor = (value: number) => {
  if (value >= 50) return 'text-quality-legendary'
  if (value > 33) return 'text-quality-epic'
  if (value > 22) return 'text-quality-rare'
  if (value > 11) return 'text-success-foreground'
  return 'text-muted-foreground'
}

const numericSorting: SortingFn<EnrichedTeamMembership> = (
  rowA,
  rowB,
  columnId,
) =>
  (Number(rowA.getValue(columnId)) || 0) -
  (Number(rowB.getValue(columnId)) || 0)

export const computeRankById = (
  members: EnrichedTeamMembership[],
  accessor: (member: EnrichedTeamMembership) => number,
  inverted = false,
): Record<string, number> | undefined => {
  if (!members.some((row) => row.data)) {
    return undefined
  }

  const withTotals = members.map((row) => ({
    id: row.id,
    total: accessor(row),
  }))

  const sorted = [...withTotals].sort((a, b) =>
    inverted ? a.total - b.total : b.total - a.total,
  )

  return Object.fromEntries(
    withTotals.map((row) => [
      row.id,
      sorted.findIndex((r) => r.total === row.total) + 1,
    ]),
  )
}

export const createRankColumn = (
  rankById: Record<string, number> | undefined,
  id = 'rank',
  className = 'border-r',
): ColumnDef<EnrichedTeamMembership> => ({
  id,
  size: 75,
  accessorFn: (row) => rankById?.[row.id.toString()] ?? 0,
  ...genericSorting<EnrichedTeamMembership>('alphanumeric', 'Rank'),
  meta: { className },
  cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
    const value = rankById?.[info.row.original.id.toString()]
    return value ? (
      <div className={cn('flex items-center justify-center gap-2')}>
        <Medal className={cn('w-4 h-4', rankToColor(value))} /> {value}
      </div>
    ) : (
      ''
    )
  },
})

export const useSimpleRanks = (
  config: ColumnGroup[],
  dependencies: unknown[] = [],
) => {
  const { visibleMembers } = useAudit()

  return useMemo(
    () =>
      Object.fromEntries(
        config.map((group) => [
          group.id,
          computeRankById(
            visibleMembers,
            (member) => {
              const fields = group.rankFields ?? [group.subColumns[0].field]
              const values = fields.map(
                (field) =>
                  Number(member.data?.[field ?? ''] ?? 0) *
                  (group.rankWeights?.[field ?? ''] ?? 1),
              )
              if (group.rankMode === 'max') return Math.max(...values)
              if (group.rankMode === 'sequential')
                return values.reduce(
                  (composite, v, i) =>
                    composite + v * Math.pow(10000, values.length - 1 - i),
                  0,
                )
              return values.reduce((sum, v) => sum + v, 0)
            },
            group.rankInverted,
          ),
        ]),
      ),
    [visibleMembers, ...dependencies],
  )
}

export const useSimpleColumns = (
  config: ColumnGroup[],
  dependencies: unknown[] = [],
): ColumnDef<EnrichedTeamMembership>[] => {
  const { showingRanks } = useAudit()
  const ranksByGroup = useSimpleRanks(config, dependencies)

  return useMemo(
    () =>
      config.map((group, groupIndex) => ({
        id: group.id,
        header: group.header,
        meta: {
          className: cn(
            groupIndex > 0 ? 'border-l' : '',
            group.meta?.className,
          ),
        },
        columns: [
          ...(showingRanks && group.rankFields !== null
            ? [
                createRankColumn(
                  group.rankById ?? ranksByGroup[group.id],
                  `${group.id}_rank`,
                  groupIndex > 0 ? 'border-l' : '',
                ),
              ]
            : []),
          ...group.subColumns.map((sub, index) => ({
            id: sub.id,
            ...(sub.field
              ? genericSorting<EnrichedTeamMembership>(
                  sub.numeric ? numericSorting : 'alphanumeric',
                  typeof sub.header === 'string' ? sub.header : sub.header(),
                  true,
                )
              : {
                  header:
                    typeof sub.header === 'string' ? sub.header : sub.header(),
                }),
            accessorFn: (row: EnrichedTeamMembership) =>
              row.data?.[sub.field ?? ''] ?? 0,
            size: sub.size,
            meta: {
              className: cn(
                index === 0 &&
                  groupIndex > 0 &&
                  (!showingRanks || group.rankFields === null)
                  ? 'border-l'
                  : '',
                sub.meta?.className,
              ),
            },
            cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
              const value = info.row.original.data?.[sub.field ?? '']
              return sub.cellWrapper ? (
                sub.cellWrapper(value as string | number, info.row.original)
              ) : (
                <span>{value}</span>
              )
            },
          })),
        ],
      })),
    [ranksByGroup, showingRanks, ...dependencies],
  )
}
